import { useEffect, useState } from "react";
import Shell from "../../components/Shell";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, Upload, FileSpreadsheet, Download as DownloadIcon } from "lucide-react";
import AuthImage from "../../components/AuthImage";

const EMPTY = { brand_id: "", name: "", sku: "", description: "", price: 0, currency: "NZD", image_path: null, pdf_paths: [], is_active: true };

export default function AdminProducts() {
    const [list, setList] = useState([]);
    const [brands, setBrands] = useState([]);
    const [editing, setEditing] = useState(null);

    const reload = async () => {
        const [pl, bl] = await Promise.all([api.get("/admin/products"), api.get("/brands")]);
        setList(pl.data || []);
        setBrands(bl.data || []);
    };
    useEffect(() => { reload(); }, []);

    const save = async () => {
        try {
            const payload = { ...editing, price: parseFloat(editing.price || 0) };
            if (editing.id) {
                await api.put(`/admin/products/${editing.id}`, payload);
                toast.success("Product updated");
            } else {
                await api.post("/admin/products", payload);
                toast.success("Product created");
            }
            setEditing(null);
            reload();
        } catch (err) { toast.error(formatApiError(err)); }
    };
    const del = async (id) => {
        if (!window.confirm("Delete this product?")) return;
        await api.delete(`/admin/products/${id}`);
        toast.success("Deleted");
        reload();
    };

    const uploadImage = async (file) => {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("kind", "image");
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setEditing(e => ({ ...e, image_path: data.path }));
        toast.success("Image uploaded");
    };
    const uploadPdf = async (file) => {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("kind", "pdf");
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setEditing(e => ({ ...e, pdf_paths: [...(e.pdf_paths || []), { path: data.path, title: data.filename, filename: data.filename }] }));
        toast.success("PDF uploaded");
    };

    return (
        <Shell variant="admin">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10 animate-fade-in">
                <div className="flex items-baseline justify-between mb-10">
                    <div>
                        <p className="eyebrow mb-2">Catalogue</p>
                        <h1 className="font-heading text-3xl sm:text-4xl font-light tracking-tight">Products</h1>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                    <button data-testid="new-product-btn" onClick={() => setEditing({ ...EMPTY, brand_id: brands[0]?.id || "" })} className="btn-primary"><Plus size={14} strokeWidth={1.5} /> New product</button>
                    <a data-testid="csv-template" href="data:text/csv;charset=utf-8,brand,name,sku,description,price,currency,is_active%0Ajalupro,Jalupro%20Classic,JCC02,Classic%20bio-revitalisation,195,NZD,true" download="dermalis-products-template.csv" className="btn-secondary"><DownloadIcon size={14} strokeWidth={1.5} /> CSV template</a>
                    <label className="btn-secondary cursor-pointer">
                        <FileSpreadsheet size={14} strokeWidth={1.5} /> Import CSV
                        <input data-testid="csv-import" type="file" accept=".csv,text/csv" hidden onChange={async (e) => {
                            const file = e.target.files?.[0]; if (!file) return;
                            const fd = new FormData(); fd.append("file", file);
                            try {
                                const { data } = await api.post("/admin/products/import-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
                                toast.success(`Imported · created ${data.created} · updated ${data.updated}${data.errors.length ? ` · ${data.errors.length} skipped` : ""}`);
                                if (data.errors.length) console.warn("CSV errors:", data.errors);
                                reload();
                            } catch (err) { toast.error(formatApiError(err)); }
                            e.target.value = "";
                        }} />
                    </label>
                    </div>
                </div>

                <div className="card-base overflow-hidden" data-testid="products-table">
                    <table className="w-full text-left">
                        <thead className="bg-zinc-50/60 border-b border-zinc-200">
                            <tr className="text-[10px] uppercase tracking-widest text-zinc-500">
                                <th className="py-3 px-4">Product</th><th className="py-3 px-4">Brand</th><th className="py-3 px-4">SKU</th><th className="py-3 px-4">Price</th><th className="py-3 px-4">Active</th><th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {list.map(p => (
                                <tr key={p.id} className="border-b border-zinc-100">
                                    <td className="py-3 px-4 text-sm font-medium">{p.name}</td>
                                    <td className="py-3 px-4 text-sm text-zinc-600">{brands.find(b => b.id === p.brand_id)?.name || "—"}</td>
                                    <td className="py-3 px-4 text-sm text-zinc-600">{p.sku}</td>
                                    <td className="py-3 px-4 text-sm">${(p.price || 0).toFixed(2)}</td>
                                    <td className="py-3 px-4 text-xs">{p.is_active ? "Active" : "Hidden"}</td>
                                    <td className="py-3 px-4 text-right">
                                        <button data-testid={`product-edit-${p.id}`} onClick={() => setEditing(p)} className="btn-ghost"><Pencil size={14} strokeWidth={1.5} /></button>
                                        <button data-testid={`product-delete-${p.id}`} onClick={() => del(p.id)} className="btn-ghost text-red-700"><Trash2 size={14} strokeWidth={1.5} /></button>
                                    </td>
                                </tr>
                            ))}
                            {list.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-sm text-zinc-500">No products yet. Click "New product" to add.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>

            {editing && (
                <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={() => setEditing(null)}>
                    <div className="bg-white w-full max-w-xl h-full overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
                        <p className="eyebrow mb-1">{editing.id ? "Edit product" : "New product"}</p>
                        <h2 className="font-heading text-2xl mb-6">{editing.name || "Untitled"}</h2>

                        <div className="space-y-4">
                            <div>
                                <label className="input-label">Brand</label>
                                <select data-testid="product-brand" value={editing.brand_id} onChange={(e) => setEditing({ ...editing, brand_id: e.target.value })} className="input-base">
                                    <option value="">— Select brand —</option>
                                    {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                                </select>
                            </div>
                            <div><label className="input-label">Name</label><input data-testid="product-name" className="input-base" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
                            <div><label className="input-label">SKU</label><input data-testid="product-sku" className="input-base" value={editing.sku} onChange={(e) => setEditing({ ...editing, sku: e.target.value })} /></div>
                            <div><label className="input-label">Price (NZD)</label><input data-testid="product-price" type="number" step="0.01" className="input-base" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} /></div>
                            <div><label className="input-label">Description</label><textarea data-testid="product-description" rows={5} className="input-base" value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></div>

                            <div>
                                <label className="input-label">Image</label>
                                <div className="flex items-center gap-4">
                                    <div className="w-24 h-24 bg-zinc-50 border border-zinc-200 flex-shrink-0">
                                        <AuthImage path={editing.image_path} alt="" className="w-full h-full object-contain p-2" />
                                    </div>
                                    <label className="btn-secondary cursor-pointer">
                                        <Upload size={14} strokeWidth={1.5} /> Upload
                                        <input data-testid="product-image-upload" type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0])} />
                                    </label>
                                </div>
                            </div>

                            <div>
                                <label className="input-label">PDF resources</label>
                                <div className="space-y-2 mb-3">
                                    {(editing.pdf_paths || []).map((f, i) => (
                                        <div key={i} className="flex items-center justify-between border border-zinc-200 px-3 py-2 rounded-sm text-sm">
                                            <span>{f.title || f.filename}</span>
                                            <button onClick={() => setEditing({ ...editing, pdf_paths: editing.pdf_paths.filter((_, j) => j !== i) })} className="text-red-700"><Trash2 size={14} strokeWidth={1.5} /></button>
                                        </div>
                                    ))}
                                </div>
                                <label className="btn-secondary cursor-pointer">
                                    <Upload size={14} strokeWidth={1.5} /> Upload PDF
                                    <input data-testid="product-pdf-upload" type="file" accept="application/pdf" hidden onChange={(e) => e.target.files?.[0] && uploadPdf(e.target.files[0])} />
                                </label>
                            </div>

                            <label className="flex items-center gap-2 text-sm">
                                <input data-testid="product-active" type="checkbox" checked={editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} /> Visible to clinics
                            </label>
                        </div>

                        <div className="flex gap-3 mt-8 pt-6 border-t border-zinc-200">
                            <button data-testid="product-save" onClick={save} className="btn-primary">Save</button>
                            <button onClick={() => setEditing(null)} className="btn-ghost ml-auto">Cancel</button>
                        </div>
                    </div>
                </div>
            )}
        </Shell>
    );
}
